import type { Meal, Unit } from '../../domain/types.js';
import type { ItemMeta } from '../sync/merge.js';

export type Rating = 'loved' | 'fine' | 'never';

export interface CookLogEntry {
  mealId: string;
  cookedOn: string;
  /**
   * `null` means ASKED AND DISMISSED, not a neutral opinion. It is a real
   * answer and is recorded as one — Phase 4's taste profile must filter
   * `rating !== null` before aggregating, or every skip reads as a data point.
   */
  rating: Rating | null;
}

export interface StapleDefault {
  ingredientId: string;
  qty: number;
  unit: Unit;
  /** Pre-ticked on a new plan, so the usual shop is a glance not a re-entry. */
  usuallyOn: boolean;
}

export interface Library {
  favourites: string[];
  cookLog: CookLogEntry[];
  staples: StapleDefault[];
  customMeals: CustomMeal[];
  /** See `StoredPlan.itemMeta`. Optional so a v1 library still loads. */
  itemMeta?: Record<string, ItemMeta>;
  updatedAt?: string;
}

/** The standing list. Everything here is bought most weeks. */
export const DEFAULT_STAPLES: StapleDefault[] = [
  { ingredientId: 'milk', qty: 4544, unit: 'ml', usuallyOn: true },
  { ingredientId: 'bread-white', qty: 16, unit: 'each', usuallyOn: true },
  { ingredientId: 'eggs', qty: 12, unit: 'each', usuallyOn: true },
  { ingredientId: 'butter', qty: 250, unit: 'g', usuallyOn: true },
  { ingredientId: 'cheddar', qty: 400, unit: 'g', usuallyOn: true },
  { ingredientId: 'yoghurt-pots', qty: 12, unit: 'each', usuallyOn: true },
  { ingredientId: 'bananas', qty: 7, unit: 'each', usuallyOn: true },
  { ingredientId: 'apples', qty: 6, unit: 'each', usuallyOn: true },
  { ingredientId: 'cereal', qty: 750, unit: 'g', usuallyOn: true },
  { ingredientId: 'orange-juice', qty: 1000, unit: 'ml', usuallyOn: true },
  { ingredientId: 'crisps', qty: 12, unit: 'each', usuallyOn: false },
  { ingredientId: 'biscuits', qty: 300, unit: 'g', usuallyOn: false },
  { ingredientId: 'cereal-bars', qty: 6, unit: 'each', usuallyOn: false },
  { ingredientId: 'raisins', qty: 200, unit: 'g', usuallyOn: false },
];

export const EMPTY_LIBRARY: Library = {
  favourites: [],
  cookLog: [],
  staples: DEFAULT_STAPLES,
  customMeals: [],
};

/**
 * A household's own recipe. It IS a Meal, so the engine, picker and list treat
 * it exactly like a bundled one; the extra fields only serve the form and sync.
 */
export interface CustomMeal extends Meal {
  /** What the person typed. Kept so the form re-opens as they entered it. */
  serves: number;
  /**
   * Never hard-deleted. A planned week that names a deleted recipe would
   * throw "Unknown meal" and take that week's whole shopping list with it.
   */
  archived: boolean;
  source: 'form' | 'paste-text' | 'paste-url';
  sourceUrl: string | null;
  /** Device id, as Phase 3 item meta uses. */
  createdBy: string;
}

export const CUSTOM_PREFIX = 'custom-';

export function isCustomId(id: string): boolean {
  return id.startsWith(CUSTOM_PREFIX);
}

/** Random, so two phones creating recipes offline can never collide. */
export function newCustomMealId(): string {
  const webCrypto = globalThis.crypto;
  const raw = typeof webCrypto?.randomUUID === 'function'
    ? webCrypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  return `${CUSTOM_PREFIX}${raw}`;
}
